import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdSearch, 
  MdEdit, 
  MdDelete, 
  MdError, 
  MdFilterList, 
  MdCalendarToday, 
  MdCategory,
  MdInventory,
  MdLocationOn,
  MdClose,
  MdSave,
  MdArrowBack,
  MdWarning,
  MdBuild,
  MdArchive
} from "react-icons/md";
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const DeletedProductRecords = () => {
  const navigate = useNavigate();
  const [deletedRecords, setDeletedRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    quantity: '',
    productType: ''
  });
  const [userRole, setUserRole] = useState(null);
  const itemsPerPage = 10;



  // Check user role
  useEffect(() => {
    try {
      const token = localStorage.getItem('usibras_login');
      if (token) {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole(null);
    }
  }, []);

  // Fetch deleted records from API
  useEffect(() => {
    const fetchDeletedRecords = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/deleted-finished-products');
        if (response.data && response.data.data) {
          setDeletedRecords(response.data.data);
        } else {
          setDeletedRecords([]);
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Error loading deleted product records');
        setDeletedRecords([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDeletedRecords();
  }, []);

  // Apply filters and search
  const filteredData = deletedRecords.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    const searchableValues = [
      item.batch_id,
      item.product_type,
      item.date,
      item.deleted_at,
      item.quantity?.toString(),
      item.lane,
      item.slot,
      item.note
    ].filter(Boolean);

    const matchesSearch = searchTermLower === '' || 
      searchableValues.some(value => 
        value.toLowerCase().includes(searchTermLower)
      );

    const matchesDate = (!filters.startDate || new Date(item.deleted_at) >= new Date(filters.startDate)) &&
                       (!filters.endDate || new Date(item.deleted_at) <= new Date(filters.endDate));
    
    const matchesQuantity = !filters.quantity || item.quantity?.toString() === filters.quantity;
    
    const matchesProductType = !filters.productType || item.product_type === filters.productType;

    return matchesSearch && matchesDate && matchesQuantity && matchesProductType;
  });

  // Calculate summary statistics
  const totalQuantity = filteredData.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
  const totalRecords = filteredData.length;

  // Get unique product types for filter
  const uniqueProductTypes = [...new Set(deletedRecords.map(item => item.product_type))].filter(Boolean);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const TABLE_HEAD = ["Batch ID", "Product Type", "Quantity", "Location", "Storage Date", "Deletion Date", "Deletion Note"];

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {/* Header with back button */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              <MdArrowBack className="h-5 w-5" />
              Back
            </button>
            <div>
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                Deleted Product Records
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                Detailed view of all deleted product records and their deletion notes
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-red-50 p-3 dark:bg-navy-700">
                <MdArchive className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Deleted Quantity</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{totalQuantity} boxes</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-orange-50 p-3 dark:bg-navy-700">
                <MdDelete className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Deleted Records</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{totalRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Product Types</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{uniqueProductTypes.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-white">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Quantity</label>
              <input
                type="number"
                value={filters.quantity}
                onChange={(e) => setFilters({...filters, quantity: e.target.value})}
                placeholder="Filter by quantity"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Product Type</label>
              <select
                value={filters.productType}
                onChange={(e) => setFilters({...filters, productType: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Product Types</option>
                {uniqueProductTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setFilters({startDate: '', endDate: '', quantity: '', productType: ''})}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table header and search */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-10 items-center rounded-full bg-white px-4 dark:bg-navy-900">
              <span className="text-xl">
                <MdSearch className="h-5 w-5 text-gray-400" />
              </span>
              <input
                type="text"
                placeholder="Search across all fields..."
                className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-[10px] pr-4 text-start dark:!border-navy-700"
                    >
                      <p className="text-sm font-bold tracking-wide text-gray-800 dark:text-white">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.batch_id}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.product_type}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.quantity} boxes
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.lane}, Slot {item.slot}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {new Date(item.date).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {new Date(item.deleted_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white max-w-[200px] truncate" title={item.note}>
                        {item.note || '-'}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            
            {/* Page number buttons */}
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                    currentPage === pageNum
                      ? "bg-brand-700 text-white"
                      : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DeletedProductRecords; 